import { useRef, useState } from "react";
import { cn } from "@/cn";
import {
  useArchiveDocument,
  useDocuments,
  useDownloadDocument,
  useUpdateDocument,
  useUploadDocument,
} from "@/hooks/useDocuments";
import type { DocumentDocType } from "@/api/documents";

const DOC_TYPE_OPTIONS: { value: DocumentDocType; label: string }[] = [
  { value: "cv", label: "CV / Resume" },
  { value: "cover_letter", label: "Cover letter" },
  { value: "portfolio", label: "Portfolio" },
  { value: "company_research", label: "Company research" },
  { value: "other", label: "Other" },
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentLibraryPanel({
  linkedCompanyId,
  linkedJobId,
  defaultDocType = "cv",
  compact = false,
}: {
  linkedCompanyId?: string;
  linkedJobId?: string;
  defaultDocType?: DocumentDocType;
  compact?: boolean;
}) {
  const { data: documents = [], isLoading, error } = useDocuments({
    companyId: linkedCompanyId,
    jobId: linkedJobId,
  });
  const upload = useUploadDocument();
  const updateDoc = useUpdateDocument();
  const archive = useArchiveDocument();
  const download = useDownloadDocument();
  const fileRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<DocumentDocType>(defaultDocType);
  const [status, setStatus] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setStatus(null);
    try {
      await upload.mutateAsync({
        file,
        docType,
        linkedCompanyId,
        linkedJobId,
        isDefault: docType === "cv",
      });
      setStatus("Uploaded");
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Upload failed");
    }
  }

  return (
    <section
      className={cn(
        "rounded-lg border border-border bg-card",
        compact ? "p-3" : "p-4",
      )}
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3
            className={cn(
              "font-semibold text-foreground",
              compact ? "text-xs uppercase tracking-wide text-muted-foreground" : "text-sm",
            )}
          >
            Document library
          </h3>
          {!compact && (
            <p className="mt-1 text-xs text-muted-foreground">
              Files linked to this job/company. Manage your full library in
              Assets. Separate from the Markdown CV in AI Context.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value as DocumentDocType)}
            className="rounded-md border border-border bg-bg px-2 py-1.5 text-xs"
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
          >
            {upload.isPending ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.md,.txt,.png,.jpg,.jpeg,.webp"
            onChange={(e) => void handleFileChange(e)}
          />
        </div>
      </div>

      {status && (
        <p className="mb-2 text-xs text-muted-foreground">{status}</p>
      )}

      {isLoading && (
        <p className="text-xs text-muted-foreground">Loading documents…</p>
      )}
      {error && (
        <p className="text-xs text-destructive">Failed to load documents.</p>
      )}

      {!isLoading && documents.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No documents yet. Upload a PDF CV to get started.
        </p>
      )}

      <ul className="space-y-2">
        {documents.map((doc) => (
          <li
            key={doc.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-bg/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">
                  {doc.originalFileName ?? doc.fileName}
                </span>
                {doc.isDefault && (
                  <span className="rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand">
                    Default CV
                  </span>
                )}
                <span className="text-[10px] uppercase text-muted-foreground">
                  {doc.docType}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {formatBytes(doc.fileSize)} · {doc.mimeType}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {doc.docType === "cv" && !doc.isDefault && (
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 text-[11px] hover:bg-muted/20"
                  disabled={updateDoc.isPending}
                  onClick={() =>
                    void updateDoc.mutateAsync({
                      docId: doc.id,
                      patch: { isDefault: true },
                    })
                  }
                >
                  Set default
                </button>
              )}
              <button
                type="button"
                className="rounded border border-border px-2 py-1 text-[11px] hover:bg-muted/20"
                disabled={download.isPending}
                onClick={() => void download.mutateAsync(doc.id)}
              >
                Download
              </button>
              <button
                type="button"
                className="rounded border border-border px-2 py-1 text-[11px] text-destructive hover:bg-destructive/10"
                disabled={archive.isPending}
                onClick={() => void archive.mutateAsync(doc.id)}
              >
                Archive
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
